/**
 * Split a monthly gross across the structure's selected components
 * by evaluating each component's existing formula.
 * MONTHLY_CTC, GROSS, and CTC in a formula mean the monthly gross entered on this page.
 *
 * Accounting buckets (never mix):
 *   earnings → Gross
 *   employee deductions → Net = Gross − deductions
 *   employer contributions → may increase CTC/package, never Net
 */

function expressionOf(comp, formulas) {
  const id = String(comp?.component_id || "");
  const saved = formulas?.[id]?.expression;
  return String(saved || comp?.formula_expression || comp?.formula || "").trim();
}

const EMPLOYER_CODES = new Set([
  "PF_EMPLOYER",
  "EMP_PF",
  "EMPLOYER_PF",
  "ER_PF",
  "ESI_EMPLOYER",
  "EMP_ESI",
  "ER_ESI",
  "PF_ADMIN",
  "PF_EDLI",
  "PF_EPS",
  "PF_EPF",
  "PF_INSPECTION",
  "LWF_EMPLOYER",
  "EDLI",
  "ADMIN_CHARGES",
]);

const EMPLOYEE_DED_CODES = new Set([
  "PF",
  "PF_EMPLOYEE",
  "EMPLOYEE_PF",
  "ESI",
  "ESI_EMPLOYEE",
  "EMPLOYEE_ESI",
  "PT",
  "PROF_TAX",
  "TDS",
  "LWF",
  "NPS",
  "VPF",
]);

export function componentBucket(comp) {
  const cat = String(comp?.component_category || comp?.component_type || "").toLowerCase();
  const code = String(comp?.code || "").toUpperCase().replace(/\s+/g, "_");
  if (EMPLOYER_CODES.has(code) || cat.includes("employer")) return "employer";
  if (EMPLOYEE_DED_CODES.has(code) || cat === "deduction" || cat === "statutory") return "deduction";
  if (cat.startsWith("earn") || cat === "benefit" || cat === "reimbursement") return "earning";
  if (cat.includes("deduct") || cat.includes("stat")) return "deduction";
  return "earning";
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export function readableFormula(expression) {
  return String(expression || "")
    .replace(/MONTHLY_CTC/gi, "Gross salary")
    .replace(/ANNUAL_CTC/gi, "Annual gross salary")
    .replace(/\bCTC\b/gi, "Gross salary");
}

function evalExpr(expression, scope, pendingCodes) {
  const waiting = [];
  const replaced = String(expression || "").replace(/[A-Za-z_][A-Za-z0-9_]*/g, (name) => {
    const key = name.toUpperCase();
    if (key === "MIN" || key === "MAX" || key === "ROUND") return key.toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(scope, key)) {
      if (pendingCodes.has(key)) {
        waiting.push(key);
        return "0";
      }
      return "(0)";
    }
    return `(${Number(scope[key])})`;
  });
  if (waiting.length) return { ok: false, unknown: waiting };
  if (!/^[\d.+\-*/(),\se]+$/i.test(replaced.replace(/min|max|round/g, ""))) {
    return { ok: false, unknown: ["syntax"] };
  }
  const js = replaced
    .replace(/\bmin\(/g, "Math.min(")
    .replace(/\bmax\(/g, "Math.max(")
    .replace(/\bround\(/g, "Math.round(");
  try {
    const value = Function(`"use strict"; return (${js});`)();
    if (!Number.isFinite(Number(value))) return { ok: false };
    return { ok: true, value: round2(value) };
  } catch {
    return { ok: false };
  }
}

function money(n) {
  return INR.format(Math.round(Number(n) || 0));
}

function calculationText(expression, scope, amount, usingMoney) {
  const raw = String(expression || "").trim();
  if (!raw) return "No formula";
  if (!usingMoney) return readableFormula(raw);
  if (/^\d+(?:\.\d+)?$/.test(raw.replace(/\s+/g, ""))) {
    return `Fixed ${money(amount)}`;
  }
  let text = raw;
  const names = Object.keys(scope).sort((a, b) => b.length - a.length);
  for (const name of names) {
    if (name === "MIN" || name === "MAX" || name === "ROUND") continue;
    text = text.replace(new RegExp(`\\b${name}\\b`, "gi"), money(scope[name]));
  }
  text = text.replace(/\b[A-Za-z_][A-Za-z0-9_]*\b/g, (name) => {
    const key = name.toUpperCase();
    if (key === "MIN" || key === "MAX" || key === "ROUND") return name.toLowerCase();
    return money(0);
  });
  text = text.replace(/\*/g, "×");
  return `${text} = ${money(amount)}`;
}

export function buildGrossAllocation(components = [], formulas = {}, monthlyGross = null) {
  const gross = Number(monthlyGross);
  const usingMoney = Number.isFinite(gross) && gross > 0;
  const base = usingMoney ? gross : 100;

  const scope = {
    MONTHLY_CTC: base,
    GROSS: base,
    CTC: base,
    ANNUAL_CTC: usingMoney ? round2(base * 12) : 100,
  };

  const rows = (components || []).map((comp) => {
    const expression = expressionOf(comp, formulas);
    const code = String(comp.code || "").toUpperCase();
    const bucket = componentBucket(comp);
    return {
      id: String(comp.component_id),
      name: comp.name || comp.code || "Component",
      code,
      bucket,
      earning: bucket === "earning",
      employer: bucket === "employer",
      expression,
      amount: 0,
      percent: 0,
      note: readableFormula(expression) || "Formula not set",
      calculation: "",
      resolved: false,
    };
  });

  for (let pass = 0; pass < rows.length + 2; pass += 1) {
    let progressed = false;
    for (const row of rows) {
      if (row.resolved) continue;
      const expr = row.expression.replace(/\s+/g, " ").trim();
      if (!expr) {
        row.resolved = true;
        row.note = "No formula";
        progressed = true;
        continue;
      }
      const pendingCodes = new Set(rows.filter((r) => !r.resolved && r.code).map((r) => r.code));
      const result = evalExpr(expr, scope, pendingCodes);
      if (!result.ok) continue;
      row.amount = result.value;
      row.resolved = true;
      row.calculation = calculationText(expr, scope, result.value, usingMoney);
      row.note = row.calculation;
      if (row.code) scope[row.code] = result.value;
      progressed = true;
    }
    if (!progressed) break;
  }

  for (const row of rows) {
    if (!row.resolved && row.expression) {
      row.note = readableFormula(row.expression);
      row.calculation = usingMoney ? "Waiting for the components this formula uses" : readableFormula(row.expression);
    }
    row.percent = base > 0 ? round2((row.amount / base) * 100) : 0;
  }

  const earnings = rows.filter((r) => r.bucket === "earning");
  const deductions = rows.filter((r) => r.bucket === "deduction");
  const employerContributions = rows.filter((r) => r.bucket === "employer");
  const earningsTotal = round2(earnings.reduce((s, r) => s + r.amount, 0));
  const deductionsTotal = round2(deductions.reduce((s, r) => s + r.amount, 0));
  const employerTotal = round2(employerContributions.reduce((s, r) => s + r.amount, 0));
  const netSalary = round2(earningsTotal - deductionsTotal);
  // Gross package balance is earnings only. Employer PF/charges are never added to Gross.
  const ctcPackage = earningsTotal;

  return {
    usingMoney,
    base,
    earnings,
    deductions,
    employerContributions,
    earningsTotal,
    deductionsTotal,
    employerTotal,
    netSalary,
    ctcPackage,
    // Legacy alias — must NOT be earnings + deductions or employer costs.
    allocatedTotal: earningsTotal,
  };
}
